import React, { useEffect, useRef, useState } from "react";
import ColumnComponent from "../../ColumnComponent/ColumnComponent";
import { RootState } from "../../../../store";
import { useSelector } from "react-redux";
import OrderBox from "../../OrderBoxV2/OrderBoxV2";
import PerfectScrollbar from "react-perfect-scrollbar";
import orderReceivedSound from "../../../../assets/sounds/jingle.wav";
import OrderDriverBox from "../../OrderDriverBox/OrderDriverBox";
import classes from "./PendingColumnDriver.module.scss";

type Props = {
  isAdmin?: boolean;
  isDriver?: boolean;
};

const PendingColumnDriver = ({ isAdmin, isDriver }: Props) => {
  const orderState = useSelector((state: RootState) => state.order);

  // Filter state: false = show ALL, true = exclude CREATED
  const [excludeCreated, setExcludeCreated] = useState(() => {
    const saved = localStorage.getItem("pendingColumnFilter");
    return saved === "exclude-created";
  });

  const [orders, setOrders] = useState<any>(<div>Waiting...</div>);

  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  const audioRef = useRef<HTMLAudioElement>();

  // Save filter preference when it changes
  useEffect(() => {
    localStorage.setItem(
      "pendingColumnFilter",
      excludeCreated ? "exclude-created" : "all"
    );
  }, [excludeCreated]);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(orderReceivedSound);
      audioRef.current.volume = 0.1;
      audioRef.current.loop = false;
    }

    if (isPlaying && audioRef.current) {
      audioRef.current.play();
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play();
        }
      }, 30000);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPlaying, orderReceivedSound]);

  useEffect(() => {
    if (orderState.receivedOrders.length > 0) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [orderState, setIsPlaying]);

  // Main orders processing effect
  useEffect(() => {
    if (!orderState.loadingOrders) {
      if (orderState.ordersFailed) {
        // TODO: Show error message when order loading fails
        setOrders(<div>Greška pri učitavanju porudžbina...</div>);
      } else {
        // Apply filter based on toggle state
        let filteredOrders = orderState.receivedOrders;
        if (excludeCreated) {
          filteredOrders = orderState.receivedOrders.filter(
            (order: any) => order.status !== "CREATED"
          );
        } else {
        }

        // Sort filtered orders
        let sortedOrders = [...filteredOrders];
        sortedOrders.sort((a: any, b: any) => b.id - a.id);

        // Fix: Always set orders to JSX array, never boolean
        if (sortedOrders.length > 0) {
          setOrders(
            sortedOrders.map((order: any, index: number) => {
              return isDriver ? (
                <OrderDriverBox key={`driver-${order.id}`} order={order} isAdmin={isAdmin} />
              ) : (
                <OrderBox
                  key={`order-${order.id}`}
                  order={order}
                  isAdmin={isAdmin}
                  isDriver={isDriver}
                />
              );
            })
          );
        } else {
          // Show appropriate empty state message
          setOrders(
            <div className={classes.noOrdersMessage}>
              {excludeCreated ? "Nema aktivnih porudžbina" : "Nema porudžbina"}
            </div>
          );
        }
      }
    }
    // Note: Removed orderState.loadingOrders from dependency array to prevent flicker
  }, [
    orderState.receivedOrders,
    orderState.ordersFailed,
    excludeCreated,
    isAdmin,
    isDriver,
  ]);

  // Calculate counts
  const totalCount = orderState.receivedOrders?.length || 0;
  const createdCount =
    orderState.receivedOrders?.filter(
      (order: any) => order.status === "CREATED"
    ).length || 0;
  const nonCreatedCount = totalCount - createdCount;
  const displayedCount = excludeCreated ? nonCreatedCount : totalCount;

  // Toggle component
  const filterToggle = (
    <div className={classes.filterToggle}>
      <button
        onClick={() => {
          setExcludeCreated(false);
        }}
        className={`${classes.toggleButton} ${
          !excludeCreated ? classes.toggleButtonActive : ""
        }`}
        title={`Prikaži sve porudžbine (${totalCount})`}
      >
        Sve
        <small>({totalCount})</small>
      </button>
      <button
        onClick={() => {
          setExcludeCreated(true);
        }}
        className={`${classes.toggleButton} ${
          excludeCreated ? classes.toggleButtonActive : ""
        }`}
        title={`Aktivne porudžbine (${nonCreatedCount})`}
      >
        Aktivne
        <small>({nonCreatedCount})</small>
      </button>
    </div>
  );

  return (
    <ColumnComponent
      leftSideLabel="Za prihvatanje"
      leftSideNumber={displayedCount.toString()}
      rightSide={filterToggle}
      style={{ flex: "4" }}
    >
      <PerfectScrollbar style={{ padding: "4px" }}>{orders}</PerfectScrollbar>
    </ColumnComponent>
  );
};

export default PendingColumnDriver;
